import React, { useMemo } from 'react';
import { Spin, Tooltip } from 'antd';
import {
  DatabaseOutlined, SyncOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  useMysqlServersQuery,
  useMysqlReplicationQuery,
} from '../../store/api';
import { MYSQL_SLAVE_IDS } from '../../utils/mysqlReplicaServers';

const SlaveStatusItem = ({ server }) => {
  const navigate = useNavigate();
  const serverId = server?.id;
  const { data: replData, isLoading, isError, error } = useMysqlReplicationQuery(serverId, {
    pollingInterval: 60000,
    skip: !serverId,
  });

  const apiError = replData?.error;
  const repl = replData?.replication || {};
  const slave = repl.slave || {};
  const failed = isError || !!apiError;
  const ioOK = slave.io_running === 'Yes';
  const sqlOK = slave.sql_running === 'Yes';
  const lag = slave.seconds_behind != null ? parseInt(slave.seconds_behind, 10) : null;
  const overallOK = repl.is_slave && ioOK && sqlOK && (lag == null || lag < 60);

  const statusClass = isLoading ? 'warn' : failed || !repl.is_slave ? 'bad' : overallOK ? 'ok' : 'bad';

  return (
    <button
      type="button"
      className="yzen-slave-item"
      onClick={() => navigate('/mysql')}
      title="Ver monitor MySQL"
    >
      <span className={`yzen-status-dot ${statusClass}`} />
      <div className="yzen-slave-item-body">
        <div className="yzen-slave-item-name">{server.nombre}</div>
        <div className="yzen-slave-item-ip">{server.ip}</div>
      </div>
      {isLoading ? (
        <Spin size="small" />
      ) : failed ? (
        <Tooltip title={apiError || error?.data?.error || error?.message || 'Error de conexión'}>
          <CloseCircleOutlined style={{ color: '#dc3545', fontSize: 18 }} />
        </Tooltip>
      ) : repl.is_slave ? (
        <div className="yzen-slave-item-metrics">
          <span className={ioOK ? 'ok' : 'bad'} title="IO thread">
            {ioOK ? <CheckCircleOutlined /> : <CloseCircleOutlined />} IO
          </span>
          <span className={sqlOK ? 'ok' : 'bad'} title="SQL thread">
            {sqlOK ? <CheckCircleOutlined /> : <CloseCircleOutlined />} SQL
          </span>
          <span className={lag != null && lag < 30 ? 'ok' : lag != null ? 'warn' : ''} title="Lag">
            {lag != null ? `${lag}s` : '—'}
          </span>
        </div>
      ) : (
        <span className="yzen-slave-item-off">No slave</span>
      )}
    </button>
  );
};

const MysqlSlavesStatusCard = () => {
  const { data, isLoading, isFetching } = useMysqlServersQuery(undefined, { pollingInterval: 60000 });

  const slaves = useMemo(() => {
    const list = (data?.servers || []).filter((s) => s.id !== 0);
    const byId = new Map(list.map((s) => [s.id, s]));
    return MYSQL_SLAVE_IDS.map((id) => byId.get(id)).filter(Boolean);
  }, [data]);

  return (
    <div className="yzen-card yzen-slaves-card">
      <h3 className="yzen-card-title">
        <DatabaseOutlined style={{ color: '#042893' }} />
        Réplicas MySQL (slaves)
        {isFetching && <SyncOutlined spin style={{ marginLeft: 8, fontSize: 12 }} />}
      </h3>
      {isLoading && slaves.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : slaves.length === 0 ? (
        <p style={{ color: '#8392a5', fontSize: 13, margin: 0 }}>Sin slaves configurados</p>
      ) : (
        <div className="yzen-slaves-grid">
          {slaves.map((srv) => (
            <SlaveStatusItem key={srv.id} server={srv} />
          ))}
        </div>
      )}
    </div>
  );
};

export default MysqlSlavesStatusCard;
